// Sign-up rules for the Register form: what a new username, email address and
// password may contain, and the leaked-password check.
//
// These are front-end rules matched to what the API (auth/register) actually
// does, so nothing the user types is silently changed or later rejected:
//   - Lengths are checked with PHP strlen(), i.e. in UTF-8 bytes: username and
//     password at most 32 bytes. A Chinese character is 3 bytes.
//   - The password is passed through Phalcon's "string" filter before it is
//     hashed, but log-in checks the raw password. That filter drops everything
//     from a "<" onwards, turns " and ' into HTML entities and removes NUL, so
//     a password containing them could never be used to log in. They are not
//     allowed here for that reason.
//   - The email is passed through FILTER_SANITIZE_EMAIL, which drops anything
//     outside a small ASCII set, and validated with FILTER_VALIDATE_EMAIL. Only
//     addresses both leave unchanged are accepted.
//   - Log-in treats any username that looks like an email as an email, so
//     usernames cannot contain "@".
// The server still has the final say: these checks only stop bad input early.

const MAX_BYTES = 32;
const MIN_PASSWORD_CHARS = 12;
const MAX_EMAIL_BYTES = 254;

// Passed to <input passwordrules> so browser password generators suggest a
// password that passes; the allowed symbols leave out < " '.
export const PASSWORD_GENERATOR_RULES =
  `minlength: ${MIN_PASSWORD_CHARS}; maxlength: ${MAX_BYTES}; required: lower; required: upper; required: digit; ` +
  "allowed: [-_.!@#$%^&*()+=?~];";
const MAX_EMAIL_LOCAL_BYTES = 64;

// C0 and C1 control characters, including NUL, tab and newlines. Matching them
// is the point, hence the lint exception.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/;
// Characters the API's "string" filter changes (see above).
const FILTERED_PASSWORD_CHARS = /[<"']/;

// Letters in any script, digits, dot, underscore, hyphen. Built at run time so
// Babel does not expand \p{...} into a very large character class; browsers
// without Unicode property escapes fall back to ASCII letters.
const USERNAME_PATTERN = (() => {
  try {
    return new RegExp("^[\\p{L}\\p{N}._-]+$", "u");
  } catch (e) {
    return /^[A-Za-z0-9._-]+$/;
  }
})();

// ASCII only, and only characters FILTER_SANITIZE_EMAIL keeps; dot-separated
// local part; domain labels of letters, digits and inner hyphens; a TLD of
// letters. Every address this accepts also passes FILTER_VALIDATE_EMAIL.
const EMAIL_LOCAL = "[A-Za-z0-9!#$%&'*+=?^_`{|}~-]+(?:\\.[A-Za-z0-9!#$%&'*+=?^_`{|}~-]+)*";
const EMAIL_DOMAIN = "(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\\.)+[A-Za-z]{2,63}";
const EMAIL_PATTERN = new RegExp(`^${EMAIL_LOCAL}@${EMAIL_DOMAIN}$`);

/** UTF-8 length, as PHP's strlen() counts it. */
export function byteLength(value) {
  const s = String(value == null ? "" : value);
  if (typeof TextEncoder !== "undefined") return new TextEncoder().encode(s).length;
  return unescape(encodeURIComponent(s)).length;
}

/** Usernames and emails are trimmed before they are checked or sent. */
export function cleanText(value) {
  return String(value == null ? "" : value).trim();
}

/**
 * The first problem with a (cleaned) username, as an i18n key under
 * register.err, or null when it is fine.
 */
export function usernameError(username) {
  if (!username) return "usernameRequired";
  if (CONTROL_CHARS.test(username) || !USERNAME_PATTERN.test(username)) return "usernameChars";
  if (byteLength(username) > MAX_BYTES) return "usernameTooLong";
  return null;
}

/** The first problem with a (cleaned) email address, or null. */
export function emailError(email) {
  if (!email) return "emailRequired";
  if (byteLength(email) > MAX_EMAIL_BYTES) return "emailTooLong";
  if (!EMAIL_PATTERN.test(email)) return "emailInvalid";
  if (byteLength(email.split("@")[0]) > MAX_EMAIL_LOCAL_BYTES) return "emailInvalid";
  return null;
}

/**
 * The password rules that can be checked locally, in display order. Each is
 * { key, ok } where key is an i18n key under register.rule. The leaked-password
 * check is separate (pwnedCount) because it is asynchronous.
 */
export function passwordRules(password, { username = "", email = "" } = {}) {
  const pw = String(password || "");
  const chars = Array.from(pw).length;
  const bytes = byteLength(pw);
  const lower = pw.toLowerCase();
  const personal = [username, String(email).split("@")[0]]
    .map((s) => String(s || "").toLowerCase())
    .filter((s) => s.length >= 3);
  return [
    {
      // "lengthBytes" explains the case where the characters fit but the
      // bytes do not (non-English characters count as 2-4 bytes each).
      key: chars >= MIN_PASSWORD_CHARS && chars <= MAX_BYTES && bytes > MAX_BYTES ? "lengthBytes" : "length",
      ok: chars >= MIN_PASSWORD_CHARS && bytes <= MAX_BYTES,
    },
    { key: "chars", ok: pw.length > 0 && !FILTERED_PASSWORD_CHARS.test(pw) && !CONTROL_CHARS.test(pw) },
    { key: "personal", ok: pw.length > 0 && !personal.some((p) => lower.includes(p)) },
  ];
}

/**
 * How many times the password appears in Have I Been Pwned's Pwned Passwords
 * list, or null when that cannot be checked (offline, blocked, no Web Crypto).
 *
 * k-anonymity: only the first 5 hex characters of the password's SHA-1 hash
 * leave the browser; the matching suffixes come back and are compared here.
 * Add-Padding hides how many suffixes matched. fetch is used rather than the
 * app's axios instance so no app token or cookie is ever sent with it.
 */
export async function pwnedCount(password, { timeout = 5000 } = {}) {
  const subtle = window.crypto && window.crypto.subtle;
  if (!password || !subtle || !window.fetch || typeof TextEncoder === "undefined") return null;
  let timer = null;
  try {
    const digest = await subtle.digest("SHA-1", new TextEncoder().encode(password));
    const hash = Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const controller = window.AbortController ? new AbortController() : null;
    if (controller) timer = setTimeout(() => controller.abort(), timeout);
    const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      headers: { "Add-Padding": "true" },
      credentials: "omit",
      referrerPolicy: "no-referrer",
      cache: "no-store",
      signal: controller ? controller.signal : undefined,
    });
    if (!res.ok) return null;
    const body = await res.text();
    for (const line of body.split("\n")) {
      const [s, count] = line.trim().split(":");
      // Padding rows have a count of 0, so a match on one means "not leaked".
      if (s === suffix) return parseInt(count, 10) || 0;
    }
    return 0;
  } catch (e) {
    return null;
  } finally {
    if (timer) clearTimeout(timer);
  }
}
